// Canonical hub and index page template.

import React, { useMemo } from 'react';
import type { Collection } from '@airhead-games/shared/content';
import Breadcrumbs from '@/content/rendering/chrome/Breadcrumbs';
import CollectionHero from '@/content/rendering/chrome/CollectionHero';
import NavigationRail from '@/content/rendering/chrome/NavigationRail';
import { getCollectionBreadcrumbs } from '@/content/lib/breadcrumbs';
import { useContentHashNavigation } from '@/content/lib/hashNavigation';
import { buildOutline } from '@/content/lib/outline';
import { getCollectionImage } from '@/content/lib/seo';
import CollectionSectionList from '@/content/rendering/sections/CollectionSectionList';
import { Separator } from '@/components/ui/separator';
import SEO from '@/seo/SEO';

interface CollectionTemplateProps {
  collection: Collection;
}

const CollectionTemplate: React.FC<CollectionTemplateProps> = ({
  collection,
}) => {
  useContentHashNavigation();

  const breadcrumbs = useMemo(
    () => getCollectionBreadcrumbs(collection),
    [collection]
  );
  const outline = useMemo(
    () => buildOutline(collection.sections),
    [collection.sections]
  );
  const hasToc = Boolean(collection.aside?.showToc && outline.length > 0);

  return (
    <div className="min-h-svh bg-background pt-20 text-foreground sm:pt-24 md:pt-28 lg:pt-32">
      <SEO
        title={collection.seo.title ?? collection.title}
        description={collection.seo.description ?? collection.description}
        image={getCollectionImage(collection)}
        breadcrumbs={breadcrumbs.map((item) => ({
          name: item.label,
          path: item.path,
        }))}
      />
      <main className="mx-auto flex w-full max-w-screen-2xl flex-col gap-3 px-4 pb-16 sm:gap-4 sm:px-6 md:px-8 lg:gap-8 lg:px-10 xl:px-12 2xl:px-16">
        <header className="flex flex-col gap-8">
          <Breadcrumbs items={breadcrumbs} />
          <CollectionHero collection={collection} />
          <Separator />
        </header>

        {hasToc ? (
          <div className="grid gap-3 lg:gap-8 lg:grid-cols-[13rem_minmax(0,1fr)] xl:grid-cols-[15rem_minmax(0,1fr)] 2xl:grid-cols-[17rem_minmax(0,1fr)]">
            <NavigationRail outline={outline} />
            <CollectionSectionList sections={collection.sections} />
          </div>
        ) : (
          <CollectionSectionList sections={collection.sections} />
        )}
      </main>
    </div>
  );
};

export default CollectionTemplate;
